import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Calendar, Clock, Users, Plus, Edit3, Trash2, Download, 
  LogIn, LogOut, Home, ClipboardList, BarChart3, X, Check, 
  AlertCircle, ChevronRight, FileText, Mail, Type, Upload,
  CheckSquare, Square, Eye, EyeOff, ArrowUpDown, ExternalLink,
  Settings, GripVertical, Copy
} from 'lucide-react';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// SSO-Handoff aus der 90solutions-Servicecloud: qmc-Token aus #sso=... uebernehmen.
(function () {
  try {
    const m = window.location.hash.match(/[#&]sso=([^&]+)/);
    if (m && m[1]) {
      localStorage.setItem('srm_token', decodeURIComponent(m[1]));
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  } catch (e) { /* ignore */ }
})();

// Auth Context
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('srm_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.get(`${API_URL}/auth/me`)
        .then(res => {
          setUser(res.data.user);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('srm_token');
          setToken(null);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (username, password) => {
    const res = await axios.post(`${API_URL}/auth/login`, { username, password });
    const { token: newToken, user: newUser } = res.data;
    localStorage.setItem('srm_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(newUser);
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem('srm_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Navigation
const NavBar = ({ isPublic = false }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-logo">
          <ClipboardList size={20} />
          <span>SRM</span>
        </Link>
        
        {isAuthenticated ? (
          <>
            <div className="navbar-menu">
              <Link
                to="/admin/anmeldungen"
                className={`navbar-item ${location.pathname.includes('/admin/anmeldungen') ? 'active' : ''}`}
              >
                Anmeldungen
              </Link>
              <Link
                to="/admin/umfragen"
                className={`navbar-item ${location.pathname.includes('/admin/umfragen') ? 'active' : ''}`}
              >
                Umfragen
              </Link>
            </div>
            <div className="navbar-actions">
              <span className="navbar-user">{user?.username}</span>
              <button className="logout-btn" onClick={handleLogout} title="Abmelden">
                <LogOut size={18} />
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="navbar-menu">
              <Link to="/" className={`navbar-item ${location.pathname === '/' ? 'active' : ''}`}>
                Anmeldungen
              </Link>
              <Link to="/umfragen" className={`navbar-item ${location.pathname === '/umfragen' ? 'active' : ''}`}>
                Umfragen
              </Link>
            </div>
            <Link to="/login" className="login-link">
              <LogIn size={18} />
              <span>Login</span>
            </Link>
          </>
        )}
      </div>
    </nav>
  );
};

// Footer
const Footer = () => (
  <footer className="footer">
    <div className="footer-container">
      <div className="footer-left">
        <span className="footer-copyright">© 2026 SRM - Student Relations Management</span>
        <span className="footer-service">Ein Service von 90solutions</span>
      </div>
    </div>
  </footer>
);

// Login Page
const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/anmeldungen');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await login(username, password);
      navigate('/admin/anmeldungen');
    } catch (err) {
      setError(err.response?.data?.error || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>SRM</h1>
          <p className="login-desc">Student Relations Management</p>
          <p className="login-desc">Lehrstuhl BWL II</p>
        </div>
        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-message">
              <AlertCircle size={16} />
              {error}
            </div>
          )}
          <div className="form-group">
            <label>Benutzername</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Benutzername eingeben"
              required
            />
          </div>
          <div className="form-group">
            <label>Passwort</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Passwort eingeben"
              required
            />
          </div>
          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? 'Anmeldung...' : 'Anmelden'}
          </button>
        <a href="https://90solutions.de/forgot" style={{display:'block',textAlign:'center',marginTop:'14px',fontSize:'13px',color:'#eb811c',textDecoration:'none',fontWeight:600}}>Passwort vergessen?</a>
        </form>
      </div>
    </div>
  );
};

// Public Events List
const PublicEventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${API_URL}/events`);
        setEvents(res.data.events);
      } catch (err) {
        console.error('Error fetching events:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="public-page">
      <div className="page-header">
        <h1>Aktuelle Anmeldungen</h1>
        <p>Wählen Sie eine Veranstaltung aus, um sich anzumelden.</p>
      </div>
      
      <div className="events-grid">
        {events.length === 0 ? (
          <div className="empty-state">
            <Calendar size={48} />
            <p>Derzeit sind keine Anmeldungen verfügbar.</p>
          </div>
        ) : (
          events.map(event => (
            <div key={event.id} className={`event-card ${event.status}`}>
              <div className="event-card-header">
                <h3>{event.title}</h3>
                <span className={`status-badge ${event.status}`}>
                  {event.status === 'active' ? 'Aktiv' : 
                   event.status === 'upcoming' ? 'Bald verfügbar' : 'Beendet'}
                </span>
              </div>
              {event.description && (
                <p className="event-description">{event.description}</p>
              )}
              <div className="event-dates">
                {event.start_date && (
                  <div className="event-date">
                    <Calendar size={14} />
                    <span>Start: {formatDate(event.start_date)}</span>
                  </div>
                )}
                {event.end_date && (
                  <div className="event-date">
                    <Clock size={14} />
                    <span>Ende: {formatDate(event.end_date)}</span>
                  </div>
                )}
              </div>
              {event.status === 'active' ? (
                <Link to={`/${event.slug}`} className="event-link">
                  Zur Anmeldung
                  <ChevronRight size={18} />
                </Link>
              ) : event.status === 'upcoming' ? (
                <div className="event-inactive">
                  Anmeldung ab {formatDate(event.start_date)}
                </div>
              ) : (
                <div className="event-inactive">
                  Anmeldung beendet
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Public Event Form Page
const PublicEventPage = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const res = await axios.get(`${API_URL}/events/${slug}`);
        setEvent(res.data.event);
        
        // Initialize form data
        const initialData = {};
        res.data.event.fields?.forEach(field => {
          initialData[field.field_name] = '';
        });
        setFormData(initialData);
      } catch (err) {
        console.error('Error fetching event:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [slug]);

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleFileChange = (fieldName, file) => {
    setFiles(prev => ({ ...prev, [fieldName]: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    
    try {
      const submitData = new FormData();
      
      // Add form fields
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value);
      });
      
      // Add files
      Object.entries(files).forEach(([key, file]) => {
        if (file) {
          submitData.append(key, file);
        }
      });
      
      await axios.post(`${API_URL}/events/${slug}/submit`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Anmeldung fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="public-page">
        <div className="error-state">
          <AlertCircle size={48} />
          <h2>Event nicht gefunden</h2>
          <p>Diese Anmeldung existiert nicht oder ist nicht mehr verfügbar.</p>
          <Link to="/" className="btn-primary">Zurück zur Übersicht</Link>
        </div>
      </div>
    );
  }

  if (event.status !== 'active') {
    return (
      <div className="public-page">
        <div className="error-state">
          <Clock size={48} />
          <h2>{event.title}</h2>
          <p>
            {event.status === 'upcoming' 
              ? `Die Anmeldung beginnt am ${new Date(event.start_date).toLocaleString('de-DE', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })} Uhr`
              : 'Die Anmeldung ist leider bereits beendet.'
            }
          </p>
          <Link to="/" className="btn-primary">Zurück zur Übersicht</Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="public-page">
        <div className="success-state">
          <Check size={48} />
          <h2>Anmeldung erfolgreich!</h2>
          <p>Vielen Dank für Ihre Anmeldung zu "{event.title}".</p>
          <Link to="/" className="btn-primary">Zurück zur Übersicht</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="public-page">
      <div className="form-page">
        <div className="form-header">
          <Link to="/" className="back-link">
            <ChevronRight size={18} className="back-icon" />
            Zurück
          </Link>
          <h1>{event.title}</h1>
          {event.description && <p>{event.description}</p>}
        </div>
        
        <form onSubmit={handleSubmit} className="signup-form">
          {error && (
            <div className="error-message">
              <AlertCircle size={16} />
              {error}
            </div>
          )}
          
          {event.fields?.map(field => (
            <div key={field.id} className="form-group">
              <label>
                {field.field_label}
                {field.is_required === 1 && <span className="required">*</span>}
              </label>
              
              {field.field_type === 'text' && (
                <input
                  type="text"
                  value={formData[field.field_name] || ''}
                  onChange={(e) => handleInputChange(field.field_name, e.target.value)}
                  required={field.is_required === 1}
                />
              )}
              
              {field.field_type === 'email' && (
                <input
                  type="email"
                  value={formData[field.field_name] || ''}
                  onChange={(e) => handleInputChange(field.field_name, e.target.value)}
                  required={field.is_required === 1}
                />
              )}
              
              {field.field_type === 'textarea' && (
                <textarea
                  value={formData[field.field_name] || ''}
                  onChange={(e) => handleInputChange(field.field_name, e.target.value)}
                  required={field.is_required === 1}
                  rows={4}
                />
              )}
              
              {field.field_type === 'file' && (
                <div className="file-input-wrapper">
                  <input
                    type="file"
                    id={`file-${field.id}`}
                    onChange={(e) => handleFileChange(field.field_name, e.target.files[0])}
                    required={field.is_required === 1}
                  />
                  <label htmlFor={`file-${field.id}`} className="file-input-label">
                    <Upload size={18} />
                    <span>{files[field.field_name]?.name || 'Datei auswählen'}</span>
                  </label>
                </div>
              )}
            </div>
          ))}
          
          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? 'Wird gesendet...' : 'Anmeldung absenden'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Admin Events Page
const AdminEventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const navigate = useNavigate();

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API_URL}/events`);
      setEvents(res.data.events);
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Möchten Sie "${title}" wirklich löschen? Alle Anmeldungen werden ebenfalls gelöscht.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/events/${id}`);
      fetchEvents();
    } catch (err) {
      alert('Löschen fehlgeschlagen');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header-row">
        <div>
          <h1>Anmeldungen verwalten</h1>
          <p>Erstellen und verwalten Sie Anmeldeformulare.</p>
        </div>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          <Plus size={18} />
          <span>Neue Anmeldung</span>
        </button>
      </div>
      
      <div className="events-table-container">
        {events.length === 0 ? (
          <div className="empty-state">
            <ClipboardList size={48} />
            <p>Noch keine Anmeldungen erstellt.</p>
            <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
              Erste Anmeldung erstellen
            </button>
          </div>
        ) : (
          <table className="events-table">
            <thead>
              <tr>
                <th>Titel</th>
                <th>URL</th>
                <th>Status</th>
                <th>Zeitraum</th>
                <th>Anmeldungen</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {events.map(event => (
                <tr key={event.id}>
                  <td className="title-cell">{event.title}</td>
                  <td className="url-cell">
                    <a href={`/${event.slug}`} target="_blank" rel="noopener noreferrer">
                      /{event.slug}
                      <ExternalLink size={14} />
                    </a>
                  </td>
                  <td>
                    <span className={`status-badge ${event.status}`}>
                      {event.status === 'active' ? 'Aktiv' : 
                       event.status === 'upcoming' ? 'Geplant' : 'Beendet'}
                    </span>
                  </td>
                  <td className="date-cell">
                    {event.start_date || event.end_date ? (
                      <>
                        {formatDate(event.start_date)} - {formatDate(event.end_date)}
                      </>
                    ) : (
                      'Unbegrenzt'
                    )}
                  </td>
                  <td className="count-cell">
                    <span className="submission-count">{event.submission_count || 0}</span>
                  </td>
                  <td className="actions-cell">
                    <button 
                      className="action-btn"
                      onClick={() => navigate(`/admin/anmeldungen/${event.id}/submissions`)}
                      title="Anmeldungen anzeigen"
                    >
                      <Users size={16} />
                    </button>
                    <button 
                      className="action-btn"
                      onClick={() => navigate(`/admin/anmeldungen/${event.id}/edit`)}
                      title="Bearbeiten"
                    >
                      <Edit3 size={16} />
                    </button>
                    <button 
                      className="action-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/${event.slug}`);
                        alert('Link kopiert!');
                      }}
                      title="Link kopieren"
                    >
                      <Copy size={16} />
                    </button>
                    <button 
                      className="action-btn delete"
                      onClick={() => handleDelete(event.id, event.title)}
                      title="Löschen"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreateModal && (
        <EventModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchEvents();
          }}
        />
      )}
    </div>
  );
};

// Event Create/Edit Modal
const EventModal = ({ event, onClose, onSuccess }) => {
  const [title, setTitle] = useState(event?.title || '');
  const [slug, setSlug] = useState(event?.slug || '');
  const [description, setDescription] = useState(event?.description || '');
  const [startDate, setStartDate] = useState(event?.start_date?.slice(0, 16) || '');
  const [endDate, setEndDate] = useState(event?.end_date?.slice(0, 16) || '');
  const [fields, setFields] = useState(event?.fields || [
    { type: 'text', label: 'Name', name: 'name', required: true },
    { type: 'email', label: 'E-Mail', name: 'email', required: true }
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[äöüß]/g, c => ({ 'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'ß': 'ss' }[c]))
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleTitleChange = (value) => {
    setTitle(value);
    if (!event) {
      setSlug(generateSlug(value));
    }
  };

  const addField = (type) => {
    const newField = {
      type,
      label: type === 'text' ? 'Textfeld' : 
             type === 'email' ? 'E-Mail' : 
             type === 'textarea' ? 'Mehrzeiliger Text' : 'Dateiupload',
      name: `field_${Date.now()}`,
      required: false
    };
    setFields([...fields, newField]);
  };

  const updateField = (index, key, value) => {
    const updated = [...fields];
    updated[index][key] = value;
    if (key === 'label') {
      updated[index].name = generateSlug(value) || `field_${index}`;
    }
    setFields(updated);
  };

  const removeField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const moveField = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= fields.length) return;
    const updated = [...fields];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setFields(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    
    try {
      const data = {
        title,
        slug,
        description,
        startDate: startDate || null,
        endDate: endDate || null,
        fields
      };
      
      if (event) {
        await axios.put(`${API_URL}/events/${event.id}`, data);
      } else {
        await axios.post(`${API_URL}/events`, data);
      }
      
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{event ? 'Anmeldung bearbeiten' : 'Neue Anmeldung erstellen'}</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div className="error-message">
                <AlertCircle size={16} />
                {error}
              </div>
            )}
            
            <div className="form-row">
              <div className="form-group">
                <label>Titel *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="z.B. Weihnachtsfeier 2026"
                  required
                />
              </div>
              <div className="form-group">
                <label>URL-Pfad *</label>
                <div className="slug-input">
                  <span className="slug-prefix">srm.90solutions.de/</span>
                  <input
                    type="text"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''))}
                    placeholder="url-pfad"
                    required
                  />
                </div>
              </div>
            </div>
            
            <div className="form-group">
              <label>Beschreibung</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optionale Beschreibung für die Anmeldung..."
                rows={2}
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Anmeldung möglich ab</label>
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Anmeldung möglich bis</label>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
            
            <div className="form-section">
              <h3>Formularfelder</h3>
              <div className="field-list">
                {fields.map((field, index) => (
                  <div key={index} className="field-item">
                    <div className="field-drag">
                      <button type="button" onClick={() => moveField(index, -1)} disabled={index === 0}>↑</button>
                      <button type="button" onClick={() => moveField(index, 1)} disabled={index === fields.length - 1}>↓</button>
                    </div>
                    <div className="field-type">
                      {field.type === 'text' && <Type size={16} />}
                      {field.type === 'email' && <Mail size={16} />}
                      {field.type === 'textarea' && <FileText size={16} />}
                      {field.type === 'file' && <Upload size={16} />}
                    </div>
                    <input
                      type="text"
                      value={field.label}
                      onChange={(e) => updateField(index, 'label', e.target.value)}
                      placeholder="Feldbezeichnung"
                      className="field-label-input"
                    />
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateField(index, 'required', e.target.checked)}
                      />
                      <span>Pflichtfeld</span>
                    </label>
                    <button 
                      type="button" 
                      className="field-remove"
                      onClick={() => removeField(index)}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
              
              <div className="add-field-buttons">
                <button type="button" onClick={() => addField('text')} className="add-field-btn">
                  <Type size={16} />
                  <span>Textfeld</span>
                </button>
                <button type="button" onClick={() => addField('email')} className="add-field-btn">
                  <Mail size={16} />
                  <span>E-Mail</span>
                </button>
                <button type="button" onClick={() => addField('textarea')} className="add-field-btn">
                  <FileText size={16} />
                  <span>Mehrzeilig</span>
                </button>
                <button type="button" onClick={() => addField('file')} className="add-field-btn">
                  <Upload size={16} />
                  <span>Dateiupload</span>
                </button>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>Abbrechen</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Wird gespeichert...' : event ? 'Speichern' : 'Erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Event Page
const EditEventPage = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        // Get event by ID through submissions endpoint which includes fields
        const res = await axios.get(`${API_URL}/events/${id}/submissions`);
        const eventData = res.data.event;
        eventData.fields = res.data.fields.map(f => ({
          type: f.field_type,
          label: f.field_label,
          name: f.field_name,
          required: f.is_required === 1
        }));
        setEvent(eventData);
      } catch (err) {
        console.error('Error fetching event:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [id]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!event) {
    return <Navigate to="/admin/anmeldungen" />;
  }

  return (
    <EventModal
      event={event}
      onClose={() => navigate('/admin/anmeldungen')}
      onSuccess={() => navigate('/admin/anmeldungen')}
    />
  );
};

// Submissions Page
const SubmissionsPage = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('submitted_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const navigate = useNavigate();

  const fetchSubmissions = async () => {
    try {
      const res = await axios.get(`${API_URL}/events/${id}/submissions?sortBy=${sortBy}&sortOrder=${sortOrder}`);
      setData(res.data);
    } catch (err) {
      console.error('Error fetching submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, [id, sortBy, sortOrder]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handleDelete = async (submissionId) => {
    if (!window.confirm('Möchten Sie diese Anmeldung wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API_URL}/submissions/${submissionId}`);
      fetchSubmissions();
    } catch (err) {
      alert('Löschen fehlgeschlagen');
    }
  };

  const handleDownload = async (format) => {
    try {
      const response = await axios.get(`${API_URL}/events/${id}/download?format=${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${data.event.slug}-anmeldungen.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Download fehlgeschlagen');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!data) {
    return <Navigate to="/admin/anmeldungen" />;
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="admin-page">
      <div className="page-header-row">
        <div>
          <button className="back-btn" onClick={() => navigate('/admin/anmeldungen')}>
            <ChevronRight size={18} className="back-icon" />
            Zurück
          </button>
          <h1>{data.event.title}</h1>
          <p>{data.submissions.length} Anmeldungen</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => handleDownload('csv')}>
            <Download size={18} />
            <span>CSV</span>
          </button>
          <button className="btn-primary" onClick={() => handleDownload('zip')}>
            <Download size={18} />
            <span>ZIP (mit Dateien)</span>
          </button>
        </div>
      </div>
      
      <div className="submissions-table-container">
        {data.submissions.length === 0 ? (
          <div className="empty-state">
            <Users size={48} />
            <p>Noch keine Anmeldungen eingegangen.</p>
          </div>
        ) : (
          <table className="submissions-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort('id')}>
                  # {sortBy === 'id' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="sortable" onClick={() => handleSort('submitted_at')}>
                  Eingereicht {sortBy === 'submitted_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                {data.fields.map(field => (
                  <th key={field.id}>{field.field_label}</th>
                ))}
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {data.submissions.map(sub => (
                <tr key={sub.id}>
                  <td>{sub.id}</td>
                  <td className="date-cell">{formatDate(sub.submitted_at)}</td>
                  {data.fields.map(field => (
                    <td key={field.id}>
                      {field.field_type === 'file' ? (
                        sub.files?.filter(f => f.field_name === field.field_name).map(file => (
                          <span key={file.id} className="file-badge">
                            <FileText size={14} />
                            {file.original_name}
                          </span>
                        ))
                      ) : (
                        sub.data[field.field_name] || '-'
                      )}
                    </td>
                  ))}
                  <td className="actions-cell">
                    <button 
                      className="action-btn delete"
                      onClick={() => handleDelete(sub.id)}
                      title="Löschen"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// Surveys Page (placeholder)
const SurveysPage = ({ isAdmin = false }) => (
  <div className={isAdmin ? 'admin-page' : 'public-page'}>
    <div className="page-header">
      <h1>Umfragen</h1>
      <p>Diese Funktion wird bald verfügbar sein.</p>
    </div>
    <div className="empty-state">
      <BarChart3 size={48} />
      <p>Umfragen werden in einer zukünftigen Version implementiert.</p>
    </div>
  </div>
);

// Main App
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={
            <>
              <NavBar isPublic />
              <main className="main-content">
                <PublicEventsPage />
              </main>
              <Footer />
            </>
          } />
          <Route path="/umfragen" element={
            <>
              <NavBar isPublic />
              <main className="main-content">
                <SurveysPage />
              </main>
              <Footer />
            </>
          } />
          <Route path="/:slug" element={
            <>
              <NavBar isPublic />
              <main className="main-content">
                <PublicEventPage />
              </main>
              <Footer />
            </>
          } />
          
          {/* Admin Routes */}
          <Route path="/admin/*" element={
            <ProtectedRoute>
              <NavBar />
              <main className="main-content">
                <Routes>
                  <Route path="/" element={<Navigate to="/admin/anmeldungen" />} />
                  <Route path="/anmeldungen" element={<AdminEventsPage />} />
                  <Route path="/anmeldungen/:id/edit" element={<EditEventPage />} />
                  <Route path="/anmeldungen/:id/submissions" element={<SubmissionsPage />} />
                  <Route path="/umfragen" element={<SurveysPage isAdmin />} />
                </Routes>
              </main>
              <Footer />
            </ProtectedRoute>
          } />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
